"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Keeps a fading overlay mounted through one CSS opacity transition after it
 * is deactivated, and only *starts* that fade once the page behind has
 * actually painted — so we never dissolve onto a blank / skeleton frame.
 *
 * Lifecycle:
 * - `active` true  → `{ mounted: true, exiting: false }` (fully opaque).
 * - `active` flips false → stay mounted + opaque, wait a double `rAF` (the
 *   browser has committed the first frame containing the now-ready content
 *   underneath), then `exiting: true` to trigger the CSS fade, then unmount
 *   after `fadeMs`.
 * - `active` flips back true mid-fade → snap back to opaque, cancel teardown.
 *
 * `fadeMs` MUST match the CSS transition duration on the consuming element.
 */
export function useFadeOut(
  active: boolean,
  fadeMs: number,
): { mounted: boolean; exiting: boolean } {
  const [mounted, setMounted] = useState(active);
  const [exiting, setExiting] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      setMounted(true);
      setExiting(false);
      return;
    }
    if (!mounted) return;

    let unmountTimer: ReturnType<typeof setTimeout> | null = null;
    const raf1 = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        setExiting(true);
        unmountTimer = setTimeout(() => setMounted(false), fadeMs);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (unmountTimer !== null) clearTimeout(unmountTimer);
    };
  }, [active, mounted, fadeMs]);

  return { mounted, exiting };
}
